import { useState } from 'react';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { CourseLimitExceededDialog } from './limit-exceeded-dialog';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { ExternalUrls, info } from '@/constants';
import { useCourseDetails } from '@/data/catalog/course';
import { useSubscriptionLimit } from '@/data/catalog/course/check-subscription-limit';
import { useSubscribeCourse } from '@/data/eduplay/courses/subscribe';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { PriceTypes, type CheckoutConfig } from '@/models/catalog/course';
import { useOffer } from '@/packages/courses/pages/details/context/offer';
import { formatMoney } from '@/utils/format-money';

interface RegisterContentProps {
  checkoutConfig: CheckoutConfig;
  hasExtraPayment: boolean;
  salePrice?: number | null;
}

const getChargeValue = (checkoutConfig: CheckoutConfig, price: number) => {
  const { has_extra_payment, charging_amount, charging_percentage } =
    checkoutConfig;

  if (!has_extra_payment) return null;
  if (charging_amount) return formatMoney(charging_amount);
  if (charging_percentage)
    return formatMoney((price * charging_percentage) / 100);
};

const RegisterContent = ({
  checkoutConfig,
  hasExtraPayment,
  salePrice,
}: RegisterContentProps) => {
  if (!salePrice || salePrice === 0) return null;

  const chargeValue = getChargeValue(checkoutConfig, salePrice);

  const content = hasExtraPayment
    ? {
        title: 'Taxa de matrícula',
        description: `
        Para iniciar o processo de matrícula, será cobrada uma taxa
        de ${chargeValue}. Esse valor não é descontado das mensalidades do curso.`,
        refund: `
        Caso desista de realizar a matrícula, você pode solicitar a devolução da
        taxa de matrícula.`,
        refundLink: `${ExternalUrls.GalenaService}/articles/25040094299917-Qual-o-prazo-para-solicitar-um-cancelamento`,
        refundLinkTitle: 'Veja os prazos aqui',
      }
    : {
        title: 'Matrícula',
        description: `
        Para iniciar o processo de matrícula você paga o valor da primeira mensalidade do semestre agora.
        A partir da segunda mensalidade, os pagamentos são feitos diretamente para a instituição de ensino.`,
        refund: `
        Caso desista de realizar a matrícula, você pode solicitar a devolução da primeira mensalidade, conforme
      `,
        refundLink: `${ExternalUrls.GalenaService}/articles/25040094299917-Qual-o-prazo-para-solicitar-um-cancelamento`,
        refundLinkTitle: 'condições e prazos',
      };

  return (
    <Stack gap={1} p={2} bgcolor="info.light" borderRadius={1}>
      <Typography fontWeight={fontWeights.fontWeightSemibold}>
        {content.title}
      </Typography>
      {chargeValue ? <Typography>{chargeValue}</Typography> : null}
      <Typography>{content.description}</Typography>
      <Typography>
        {content.refund}
        <Link href={content.refundLink} target="_blank">
          {content.refundLinkTitle}
        </Link>
        .
      </Typography>
    </Stack>
  );
};

export const Details = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { details } = useCourseDetails(Number(id));
  const { isFree, isFullIncluded, cheapestOffer, checkoutConfig, pricing } =
    useOffer();
  const { canSubscribe } = useSubscriptionLimit();
  const {
    mutate: subscribeEduplayCourse,
    isPending: isSubscribingEduplayCourse,
  } = useSubscribeCourse();
  const [openCourseLimitExceededDialog, setOpenCourseLimitExceededDialog] =
    useState(false);
  const { bootstrap } = useBootstrapData();
  console.log(bootstrap?.info?.trial_at);

  const institution = details?.institution;
  const isEduplay = institution?.id === info.EduplayId;
  const isFreeEduplay = isFree && isEduplay && isFullIncluded;

  const hasExtraPayment = checkoutConfig?.has_extra_payment || false;
  const salePrice = pricing?.sale_price;

  const showRegisterContent =
    checkoutConfig && pricing?.type === PriceTypes.Tuition;

  const eduplayEnrollment = () => {
    if (!cheapestOffer?.id) return;

    subscribeEduplayCourse(cheapestOffer?.id, {
      onSuccess: () => {
        navigate(`/meu-aprendizado/curso/${cheapestOffer?.id}`);
      },
    });
  };

  const handleClick = () => {
    if (!isEduplay && isFullIncluded && !canSubscribe) {
      setOpenCourseLimitExceededDialog(true);
      return;
    }

    if (isFreeEduplay) return eduplayEnrollment();

    const url =
      cheapestOffer?.checkout_config &&
      pricing?.sale_price &&
      pricing?.sale_price > 0
        ? `/cursos/${id}/checkout/${cheapestOffer?.id}`
        : `/cursos/${cheapestOffer?.id}/inscricao`;

    const route = cheapestOffer?.id ? url : null;

    if (!route) return;
    navigate(route);
  };

  const isTrial = Boolean(bootstrap?.info?.trial_at);
  const shouldShowEnrollmentAlert =
    !cheapestOffer?.pricing_description.sale_price &&
    !isFreeEduplay &&
    !isTrial;
  return (
    <Stack gap={2}>
      {isTrial && (
        <Alert severity="info">
          Conta em modo de degustação. Solicite o acesso completo à sua empresa
          para se matricular.
        </Alert>
      )}
      {shouldShowEnrollmentAlert && (
        <Alert severity="info">
          Ao iniciar a matrícula você não se compromete com nenhum tipo de
          pagamento.
        </Alert>
      )}
      {showRegisterContent && (
        <RegisterContent
          checkoutConfig={checkoutConfig}
          hasExtraPayment={hasExtraPayment}
          salePrice={salePrice}
        />
      )}
      <Button
        variant="contained"
        onClick={handleClick}
        loading={isSubscribingEduplayCourse}
        disabled={isTrial}
      >
        {isFreeEduplay ? 'Acessar o curso' : 'Iniciar matrícula'}
      </Button>
      <Button onClick={() => window.open(ExternalUrls.GalenaService, '_blank')}>
        Ajuda
      </Button>
      {openCourseLimitExceededDialog && (
        <CourseLimitExceededDialog
          onClose={() => setOpenCourseLimitExceededDialog(false)}
        />
      )}
    </Stack>
  );
};
